// CreateRiskDialog — modal form for creating a new risk.
// Fetches taxonomy nodes for category picker.
// Auto-creates an ownership role for the current user.

import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { risksApi, type CreateRiskRequest } from '@/lib/api/risks'
import { ownershipRolesApi } from '@/lib/api/ownershipRoles'
import { taxonomyApi, type TaxonomyNode } from '@/lib/api/frameworkConfig'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { X, ChevronRight, ChevronDown } from 'lucide-react'

interface CreateRiskDialogProps {
  registerId: string
  registerName: string
  open: boolean
  onClose: () => void
  onCreated: (riskId: string) => void
}

export function CreateRiskDialog({
  registerId,
  registerName,
  open,
  onClose,
  onCreated,
}: CreateRiskDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const riskApi = risksApi(api)
  const ownApi = ownershipRolesApi(api)

  // Form state
  const [name, setName] = useState('')
  const [eventDescription, setEventDescription] = useState('')
  const [drivers, setDrivers] = useState('')
  const [impactDescription, setImpactDescription] = useState('')
  const [selectedTaxonomyNodeId, setSelectedTaxonomyNodeId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Fetch active taxonomy for the category picker
  const { data: taxonomy } = useQuery({
    queryKey: [...queryKeys.frameworkConfig.taxonomy(slug)],
    queryFn: () => taxonomyApi(api).getActive(),
    enabled: open,
  })

  // Create mutation: first create ownership role, then create risk
  const createMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTaxonomyNodeId) throw new Error('Please select a risk category')
      if (!name.trim()) throw new Error('Risk name is required')
      if (!eventDescription.trim()) throw new Error('Event description is required')

      // Step 1: Create ownership role for this risk
      const ownerRole = await ownApi.create({
        label: `Owner: ${name.trim()}`,
      })

      // Step 2: Create the risk
      const data: CreateRiskRequest = {
        name: name.trim(),
        event_description: eventDescription.trim(),
        drivers: drivers.trim() || undefined,
        impact_description: impactDescription.trim() || undefined,
        taxonomy_node_id: selectedTaxonomyNodeId,
        owner_role_id: ownerRole.id,
      }

      return riskApi.create(registerId, data)
    },
    onSuccess: (risk) => {
      // Invalidate risk list and register tree (risk counts)
      queryClient.invalidateQueries({ queryKey: queryKeys.registers.tree(slug) })
      resetForm()
      onCreated(risk.id)
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  function resetForm() {
    setName('')
    setEventDescription('')
    setDrivers('')
    setImpactDescription('')
    setSelectedTaxonomyNodeId(null)
    setError(null)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    createMutation.mutate()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold">Create Risk</h2>
            <p className="text-sm text-muted-foreground">in {registerName}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-accent">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {error && (
            <div className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="risk-name">Risk Name *</Label>
            <Input
              id="risk-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Key person dependency in engineering"
              autoFocus
            />
          </div>

          {/* Event description */}
          <div className="space-y-1.5">
            <Label htmlFor="risk-event">Event Description *</Label>
            <textarea
              id="risk-event"
              value={eventDescription}
              onChange={(e) => setEventDescription(e.target.value)}
              placeholder="What could happen?"
              className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* Drivers (optional) */}
          <div className="space-y-1.5">
            <Label htmlFor="risk-drivers">Drivers</Label>
            <textarea
              id="risk-drivers"
              value={drivers}
              onChange={(e) => setDrivers(e.target.value)}
              placeholder="What drives this risk?"
              className="min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* Impact description (optional) */}
          <div className="space-y-1.5">
            <Label htmlFor="risk-impact">Impact Description</Label>
            <textarea
              id="risk-impact"
              value={impactDescription}
              onChange={(e) => setImpactDescription(e.target.value)}
              placeholder="What would be the impact if it occurred?"
              className="min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* Taxonomy node picker */}
          <div className="space-y-1.5">
            <Label>Risk Category *</Label>
            {taxonomy ? (
              <TaxonomyPicker
                nodes={taxonomy.nodes}
                selectedId={selectedTaxonomyNodeId}
                onSelect={setSelectedTaxonomyNodeId}
              />
            ) : (
              <div className="h-32 animate-pulse rounded bg-muted/30" />
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={
              createMutation.isPending ||
              !name.trim() ||
              !eventDescription.trim() ||
              !selectedTaxonomyNodeId
            }
          >
            {createMutation.isPending ? 'Creating...' : 'Create Risk'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// --- Taxonomy picker (inline tree) ---

interface TaxonomyPickerProps {
  nodes: TaxonomyNode[]
  selectedId: string | null
  onSelect: (id: string) => void
}

interface TaxonomyTreeNode extends TaxonomyNode {
  children: TaxonomyTreeNode[]
}

function TaxonomyPicker({ nodes, selectedId, onSelect }: TaxonomyPickerProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  // Build tree from flat list
  const tree = useMemo(() => {
    const nodeMap = new Map<string, TaxonomyTreeNode>()
    const roots: TaxonomyTreeNode[] = []
    for (const n of nodes) {
      nodeMap.set(n.id, { ...n, children: [] })
    }
    for (const n of nodes) {
      const node = nodeMap.get(n.id)!
      if (n.parent_id && nodeMap.has(n.parent_id)) {
        nodeMap.get(n.parent_id)!.children.push(node)
      } else {
        roots.push(node)
      }
    }
    return roots
  }, [nodes])

  function toggle(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <div className="max-h-48 overflow-y-auto rounded-md border p-1">
      {tree.map((node) => (
        <TaxPickerNode
          key={node.id}
          node={node}
          depth={0}
          expandedIds={expandedIds}
          selectedId={selectedId}
          onToggle={toggle}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}

function TaxPickerNode({
  node,
  depth,
  expandedIds,
  selectedId,
  onToggle,
  onSelect,
}: {
  node: TaxonomyTreeNode
  depth: number
  expandedIds: Set<string>
  selectedId: string | null
  onToggle: (id: string) => void
  onSelect: (id: string) => void
}) {
  const isExpanded = expandedIds.has(node.id)
  const isSelected = node.id === selectedId
  const hasChildren = node.children.length > 0

  return (
    <div>
      <div
        className={cn(
          'flex cursor-pointer items-center gap-1 rounded-sm px-1 py-0.5 text-sm',
          'hover:bg-accent/50',
          isSelected && 'bg-primary/10 font-medium text-primary',
        )}
        style={{ paddingLeft: 4 + depth * 16 }}
        onClick={() => {
          onSelect(node.id)
          if (hasChildren && !isExpanded) {
            onToggle(node.id)
          }
        }}
      >
        {hasChildren ? (
          <button
            className="flex h-4 w-4 items-center justify-center"
            onClick={(e) => {
              e.stopPropagation()
              onToggle(node.id)
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <span className="truncate">{node.name}</span>
      </div>
      {isExpanded &&
        node.children.map((child) => (
          <TaxPickerNode
            key={child.id}
            node={child}
            depth={depth + 1}
            expandedIds={expandedIds}
            selectedId={selectedId}
            onToggle={onToggle}
            onSelect={onSelect}
          />
        ))}
    </div>
  )
}
